import { useState, useEffect, useRef } from 'react';
import { Bell, Check } from '@phosphor-icons/react';
import api from '@/lib/api';

const typeColors = {
  conversion: 'text-emerald-400',
  commission: 'text-blue-400',
  fraud: 'text-red-400',
  system: 'text-amber-400',
  invite: 'text-purple-400',
};

export default function NotificationCenter() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get('/notifications/');
      setNotifications(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      fetchNotifications();
    } catch {}
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={() => setOpen(!open)} className="relative text-zinc-400 hover:text-white transition-colors p-1">
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-80 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notification-panel">
          <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-sm font-heading font-medium text-white">Notifications</span>
            {unread > 0 && (
              <button data-testid="mark-all-read" onClick={markAllRead} className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1">
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">No notifications yet</div>
            ) : (
              notifications.slice(0, 20).map((n, i) => (
                <div key={i} className={`p-3 border-b border-zinc-800/40 hover:bg-zinc-800/30 transition-colors ${!n.read ? 'bg-blue-500/5 border-l-2 border-l-blue-500' : ''}`} data-testid={`notification-${i}`}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className={`text-xs font-medium ${typeColors[n.type] || 'text-white'}`}>{n.title}</span>
                    <span className="text-[10px] text-zinc-600">{n.created_at ? new Date(n.created_at).toLocaleTimeString() : ''}</span>
                  </div>
                  <p className="text-xs text-zinc-400">{n.message}</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
